import { useEffect, useState } from "react";
import { FlaskConical, AlertTriangle, RefreshCw } from "lucide-react";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Settings → Operations card that exposes the per-seller Test Mode toggle
 * (Shopify Bogus Gateway pattern). When ON every invoice payment and seller
 * wallet top-up is simulated — no real card / ACH / wire moves.
 *
 * Also exposes a one-click "Reset sandbox data" button that wipes ONLY
 * rows tagged is_test=true. Real data is never touched.
 */
export default function TestModeCard() {
  const [enabled, setEnabled] = useState(false);
  const [counts, setCounts]   = useState({ invoices: 0, wallet_txns: 0 });
  const [busy, setBusy]       = useState(false);
  const [resetting, setReset] = useState(false);
  const [loaded, setLoaded]   = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/tenant/test-mode");
      setEnabled(!!r.data?.enabled);
      setCounts(r.data?.sandbox_counts || { invoices: 0, wallet_txns: 0 });
    } catch (e) {
      toast.error("Could not load Test Mode state");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onToggle = async (next) => {
    setBusy(true);
    try {
      await api.post("/tenant/test-mode/toggle", { enabled: next });
      setEnabled(next);
      window.dispatchEvent(new CustomEvent("test-mode-changed"));
      toast.success(next ? "Test Mode is ON — payments are simulated." : "Test Mode is OFF.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not change Test Mode");
    } finally { setBusy(false); }
  };

  const onReset = async () => {
    if (!window.confirm(`This will permanently delete ${counts.invoices} test invoices and ${counts.wallet_txns} test transactions. Real data is not affected. Continue?`)) {
      return;
    }
    setReset(true);
    try {
      const r = await api.post("/tenant/test-mode/reset");
      toast.success(`Sandbox cleared · ${r.data.invoices_deleted} invoices · ${r.data.wallet_txns_deleted} transactions`);
      await refresh();
    } catch (e) {
      toast.error("Reset failed");
    } finally { setReset(false); }
  };

  return (
    <div data-testid="test-mode-card" className="mt-6 border border-orange-200 rounded-xl bg-orange-50/40 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
            <FlaskConical className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">Test Mode (Bogus Gateway)</div>
            <p className="text-[12.5px] text-foreground/65 mt-0.5 max-w-md">
              Simulate end-to-end payment, wire and credit flows without moving real money.
              Useful for onboarding, demos and integration testing. Every test transaction is
              tagged so you can wipe sandbox data with one click.
            </p>
          </div>
        </div>
        <Switch
          data-testid="test-mode-toggle"
          checked={enabled}
          disabled={busy || !loaded}
          onCheckedChange={onToggle}
        />
      </div>

      {enabled && (
        <div className="mt-4 p-3 rounded-md bg-orange-100/70 border border-orange-200 flex items-start gap-2 text-[12.5px] text-orange-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-[2px]" />
          <div>
            Test Mode is <strong>ON</strong>. Any invoice your buyers pay will be marked paid instantly without
            moving real money. A orange "TEST MODE" banner is visible across your portal so your team
            doesn't mistake a test for a real transaction.
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-4 text-[12.5px] text-foreground/70">
        <div data-testid="test-mode-counts">
          Sandbox: <strong>{counts.invoices}</strong> test invoice{counts.invoices === 1 ? "" : "s"}
          {" · "}
          <strong>{counts.wallet_txns}</strong> test transaction{counts.wallet_txns === 1 ? "" : "s"}
        </div>
        <Button
          data-testid="test-mode-reset"
          variant="outline"
          size="sm"
          disabled={resetting || (counts.invoices + counts.wallet_txns === 0)}
          onClick={onReset}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${resetting ? "animate-spin" : ""}`} />
          {resetting ? "Resetting…" : "Reset sandbox data"}
        </Button>
      </div>
    </div>
  );
}
